import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import ObserverSearchPage from "./pages/ObserverSearchPage";
import ObserverDirectoryPage from "./pages/ObserverDirectoryPage";
import ObserverProfilePage from "./pages/ObserverProfilePage";
import ObserverSubmitPage from "./pages/ObserverSubmitPage";
import ObserverReviewPage from "./pages/ObserverReviewPage";
import ObserverTrustPage from "./pages/ObserverTrustPage";
import ObserverAdminPage from "./pages/ObserverAdminPage";
import ObserverClaimPage from "./pages/ObserverClaimPage";
import AnalyticsConsent from "./components/AnalyticsConsent";
import { initializeAnalytics, trackPageView } from "./services/analytics";
import StaticPage from "./pages/StaticPage";
import SignInPage from "./pages/SignInPage";
import PractitionerPushPrompt from "./components/PractitionerPushPrompt";
import RouteMetadata from "./components/RouteMetadata";
import ObserverAudiencePage from "./pages/ObserverAudiencePage";
import ObserverNominatePage from "./pages/ObserverNominatePage";
import ObserverComparePage from "./pages/ObserverComparePage";

const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

const Tree: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  recaptchaSiteKey ? (
    <GoogleReCaptchaProvider
      reCaptchaKey={recaptchaSiteKey}
      scriptProps={{ async: true, defer: true }}
    >
      {children}
    </GoogleReCaptchaProvider>
  ) : (
    <>{children}</>
  );

const AnalyticsRouteTracker: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => initializeAnalytics(), []);
  React.useEffect(() => trackPageView(location.pathname), [location.pathname]);

  return <AnalyticsConsent />;
};

const App: React.FC = () => {
  return (
    <Tree>
      <BrowserRouter>
        <RouteMetadata />
        <AnalyticsRouteTracker />
        <PractitionerPushPrompt />
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Header />
          <main style={{ flex: 1 }}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<ObserverSearchPage />} />
              <Route path="/practitioners" element={<ObserverDirectoryPage />} />
              <Route
                path="/practitioners/:category"
                element={<ObserverDirectoryPage />}
              />
              <Route path="/profile/:id" element={<ObserverProfilePage />} />
              <Route path="/write-review" element={<ObserverReviewPage />} />
              <Route path="/for-practitioners" element={<ObserverAudiencePage />} />
              <Route path="/for-reviewers" element={<ObserverAudiencePage />} />
              <Route path="/nominate" element={<ObserverNominatePage />} />
              <Route path="/compare" element={<ObserverComparePage />} />
              <Route path="/report" element={<ObserverSubmitPage />} />
              <Route path="/claim" element={<ObserverClaimPage />} />
              <Route path="/admin/observer" element={<ObserverAdminPage />} />
              {[
                "/trust",
                "/trust/ranking",
                "/trust/reviews",
                "/trust/disputes",
                "/trust/seeding",
                "/trust/methodology",
                "/trust/independence",
              ].map((path) => (
                <Route key={path} path={path} element={<ObserverTrustPage />} />
              ))}
              <Route path="/sign-in" element={<SignInPage />} />
              {[
                "/about",
                "/privacy",
                "/terms",
                "/acceptable-use",
                "/safety",
                "/support",
                "/account-deletion",
              ].map((path) => (
                <Route key={path} path={path} element={<StaticPage />} />
              ))}
              <Route
                path="*"
                element={
                  <div style={{ padding: "60px", textAlign: "center" }}>
                    <h2>404 - Page Not Found</h2>
                  </div>
                }
              />
            </Routes>
          </main>
          <Footer />
        </div>
      </BrowserRouter>
    </Tree>
  );
};

export default App;
